import React from "react";
import {
  Alert,
  Button,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { z } from "zod";
import CenterScreen from "../../../components/wrappers/CenterScreen";
import LanguageSwitcher from "../../../components/LanguageSwitcher";
import { useTranslation } from "react-i18next";
import { useLanguageAwareForm } from "../../../hooks/useLanguageAwareForm";
import authApi from "../../../utils/api/authApi";
import { snackbarSeverity } from "../../../hooks/useSnackbar";
import { zodResolver } from "@hookform/resolvers/zod";
import { passwordMatcher } from "../utils/zodPasswordMatcher";
import { USER_ACTIVATION } from "../../../enums/ApiPaths";
import { TEXT_FIELDS } from "../enums/EmployeeActivationFields";
import { SnackbarContext } from "../../../contexts/Snackbar";
import { useLanguageInUrl } from "./useLanguageInUrl";

const ELSE_WIDTH = "40%";

/**
 * Clinic-created users (clients) land here from their invitation mail - no token, just the
 * path. They enter their email and a password; the backend then mails that address a
 * confirmation link (UserActivationConfirmPage), and only that activates the account.
 */
export default function UserActivationPage() {
  const { t } = useTranslation();
  const urlLanguage = useLanguageInUrl();
  // The address the confirmation mail went to, once submitted
  const [submittedEmail, setSubmittedEmail] = React.useState(null);
  const { handleSnackbar } = React.useContext(SnackbarContext);

  const {
    register,
    formState: { errors, isSubmitting },
    handleSubmit,
  } = useLanguageAwareForm({
    defaultValues: {
      email: "",
      password: "",
      confirmPassword: "",
    },
    resolver: zodResolver(
      passwordMatcher(t, {
        email: z
          .string()
          .trim()
          .email({ message: t("InvalidEmail") }),
      }),
    ),
  });

  const submitData = async (data) => {
    try {
      // Accept-Language carries the language picked here, used for the confirmation mail
      await authApi.post(USER_ACTIVATION, data);
      setSubmittedEmail(data.email);
    } catch (error) {
      handleSnackbar(error?.response?.data, snackbarSeverity.ERROR);
    }
  };

  return (
    <CenterScreen>
      <Stack
        sx={{
          width: {
            xs: "90%",
            sm: "90%",
            md: ELSE_WIDTH,
            lg: ELSE_WIDTH,
            xl: ELSE_WIDTH,
          },
        }}
        spacing={2}
      >
        <Paper sx={{ p: 3 }}>
          <Stack spacing={2} mb={2}>
            <Typography variant="h4">{t("UserActivation")}</Typography>
            <LanguageSwitcher
              defaultLanguage={urlLanguage}
              testIdPrefix="user-activation.language"
            />
          </Stack>
          {submittedEmail ? (
            <Alert severity="success" data-testid="user-activation.check-email">
              {t("UserActivationCheckEmail", { email: submittedEmail })}
            </Alert>
          ) : (
            <form onSubmit={handleSubmit(submitData)}>
              <Stack spacing={2} width={"100%"}>
                <Alert severity="info">{t("UserActivationInformation")}</Alert>
                <TextField
                  label={t("Email")}
                  variant="outlined"
                  required
                  type="email"
                  autoComplete="email"
                  {...register("email")}
                  error={!!errors?.email}
                  helperText={errors?.email?.message || ""}
                  inputProps={{ "data-testid": "user-activation.email" }}
                />
                {TEXT_FIELDS.map((field) => (
                  <TextField
                    key={field.name}
                    label={t(field.label)}
                    variant={field.variant}
                    required
                    type={"password"}
                    autoComplete="new-password"
                    {...register(field.name)}
                    error={!!errors?.[field.name]}
                    helperText={errors?.[field.name]?.message || ""}
                    inputProps={{
                      "data-testid": `user-activation.${field.name}`,
                    }}
                  />
                ))}
                <Button
                  type="submit"
                  variant="contained"
                  color="info"
                  disabled={isSubmitting}
                  data-testid="user-activation.submit-button"
                >
                  {t("UserActivationSubmit")}
                </Button>
              </Stack>
            </form>
          )}
        </Paper>
      </Stack>
    </CenterScreen>
  );
}
